import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { kbAPI } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { KbMarkdown } from '../components/KbMarkdown';
import { BookOpen, Search, Loader2, PlayCircle, Play, Video, Link2, Save, FileText, Download, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useModuleTracker } from '../hooks/useModuleTracker';
const moduleLabels = {
  general: 'General', acceso: 'Acceso y roles', dashboard: 'Dashboard', miembros: 'Miembros',
  eventos: 'Eventos', anuncios: 'Anuncios', noticias: 'Noticias', ministerios: 'Ministerios y grupos',
  cursos: 'Cursos', oraciones: 'Oraciones y peticiones', mentorias: 'Mentorías',
  moderacion: 'Moderación', aportes: 'Aportes', wallet: 'Billetera', suscripciones: 'Suscripciones',
  equipo: 'Equipo', analiticas: 'Analíticas', aliado_pastoral: 'Tu Aliado Pastoral',
  configuracion: 'Configuración', soporte: 'Soporte', operacion: 'Operación del agente',
};
const label = (m) => moduleLabels[m] || m;

const parseVideo = (url) => {
  if (!url) return null;
  const clean = url.trim();
  const yt = clean.match(
    /(?:youtube\.com\/(?:.*[?&]v=|shorts\/|live\/|embed\/|v\/)|youtu\.be\/|youtube-nocookie\.com\/embed\/)([\w-]{11})/
  );
  if (yt) return {
    src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&playsinline=1`,
    thumb: `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg`,
    watch: `https://www.youtube.com/watch?v=${yt[1]}`,
  };
  const vimeo = clean.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\?h=|\/)?([\w]+)?/);
  if (vimeo) return {
    src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1${vimeo[2] ? `&h=${vimeo[2]}` : ''}`,
    thumb: null, watch: clean,
  };
  const loom = clean.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return { src: `https://www.loom.com/embed/${loom[1]}`, thumb: null, watch: clean };
  const drive = clean.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (drive) return { src: `https://drive.google.com/file/d/${drive[1]}/preview`, thumb: null, watch: clean };
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(clean)) return { file: clean, thumb: null, watch: clean };
  return null;
};

const embedUrl = (url) => {
  const parsed = parseVideo(url);
  return parsed ? (parsed.file || parsed.src) : null;
};

const VideoMedia = ({ item, playing, onPlay }) => {
  const parsed = parseVideo(item.video_url);

  if (!item.video_url) {
    return (
      <div className="text-center text-white/60">
        <PlayCircle className="w-9 h-9 mx-auto mb-1.5" />
        <p className="text-xs">Próximamente</p>
      </div>
    );
  }
  if (!parsed) {
    return (
      <div className="text-center text-white/70 px-4" data-testid={`kb-video-external-${item.slug}`}>
        <PlayCircle className="w-9 h-9 mx-auto mb-1.5" />
        <p className="text-xs mb-2">Este enlace no se puede incrustar</p>
        <a href={item.video_url} target="_blank" rel="noreferrer" className="text-xs underline text-[#00CCFF]">
          Abrir video en otra pestaña
        </a>
      </div>
    );
  }
  if (parsed.file) {
    return <video src={parsed.file} controls className="w-full h-full" data-testid={`kb-video-file-${item.slug}`} />;
  }
  if (!playing) {
    return (
      <button
        type="button"
        onClick={onPlay}
        className="group relative w-full h-full"
        data-testid={`kb-video-play-${item.slug}`}
        aria-label={`Reproducir ${item.title}`}
      >
        {parsed.thumb && (
          <img
            src={parsed.thumb}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="absolute inset-0 w-full h-full object-cover opacity-80 transition-opacity group-hover:opacity-60"
          />
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-[#056A84]/90 group-hover:bg-[#056A84] flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
            <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
          </span>
        </span>
      </button>
    );
  }
  return (
    <iframe
      src={parsed.src}
      title={item.title}
      className="w-full h-full"
      data-testid={`kb-video-iframe-${item.slug}`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
      allowFullScreen
    />
  );
};

export default function HelpCenterPage() {
  const { isSuperAdmin, selectedChurchId } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  useModuleTracker(selectedChurchId, true);

  const [kind, setKind] = useState('article');
  const [playingSlug, setPlayingSlug] = useState(null);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const downloadKb = async () => {
    setExporting(true);
    try {
      const res = await kbAPI.export();
      const blob = new Blob([res.data.markdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'base-conocimiento-suite-ministerial.md';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Base de conocimiento descargada');
    } catch { toast.error('No se pudo generar la descarga'); }
    finally { setExporting(false); }
  };

  const fetchItems = useCallback((isActive = () => true) => {
    setLoading(true);
    kbAPI.list({
      kind,
      search: search.trim() || undefined,
      module: moduleFilter === 'all' ? undefined : moduleFilter,
      include_drafts: isSuperAdmin || undefined,
    })
      .then(res => {
        if (!isActive()) return;  // descarta respuestas obsoletas al cambiar de pestaña
        const data = res.data?.data || [];
        setItems(data);
        setModules(res.data?.modules || []);
        if (kind === 'article') setSelected(prev => data.find(d => d.slug === prev?.slug) || data[0] || null);
      })
      .catch(() => { if (isActive()) toast.error('Error al cargar el centro de ayuda'); })
      .finally(() => { if (isActive()) setLoading(false); });
  }, [kind, search, moduleFilter, isSuperAdmin]);

  useEffect(() => {
    let active = true;
    const t = setTimeout(() => fetchItems(() => active), search ? 350 : 0);
    return () => { active = false; clearTimeout(t); };
  }, [fetchItems, search]);

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      summary: item.summary || '',
      body: item.body || '',
      video_url: item.video_url || '',
      module: item.module || 'general',
      is_published: item.is_published !== false,
    });
  };

  const openCreate = () => {
    setEditing({ kind, isNew: true });
    setForm({
      title: '', summary: '', body: '', video_url: '',
      module: moduleFilter === 'all' ? 'general' : moduleFilter,
      is_published: false,
    });
  };

  const save = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) {
      toast.error('El título debe tener al menos 3 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (editing.isNew) {
        await kbAPI.create({
          title: form.title.trim(),
          summary: form.summary,
          module: form.module || 'general',
          kind: editing.kind,
          body: editing.kind === 'article' ? form.body : '',
          video_url: editing.kind === 'video' ? (form.video_url.trim() || null) : null,
          is_published: form.is_published,
        });
        toast.success(form.is_published
          ? (editing.kind === 'video' ? 'Microtutorial publicado' : 'Guía publicada')
          : 'Borrador guardado: todavía no lo ven los pastores');
      } else {
        const payload = editing.kind === 'video'
          ? { title: form.title, summary: form.summary, module: form.module, video_url: form.video_url.trim() || null, is_published: form.is_published }
          : { title: form.title, summary: form.summary, module: form.module, body: form.body, is_published: form.is_published };
        await kbAPI.update(editing.slug, payload);
        toast.success('Contenido actualizado');
      }
      setEditing(null);
      setForm(null);
      fetchItems();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await kbAPI.remove(deleting.slug);
      toast.success(deleting.kind === 'video' ? 'Microtutorial eliminado' : 'Guía eliminada');
      if (deleting.kind === 'article') setSelected(null);
      setDeleting(null);
      fetchItems();
    } catch (err) { toast.error(err?.response?.data?.detail || 'No se pudo eliminar'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <TopBar title="Centro de Ayuda" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={
          <Button variant="outline" size="sm" onClick={downloadKb} disabled={exporting} data-testid="kb-download-button">
            {exporting ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            Descargar todo
          </Button>
        }
      />

      <div className="p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar: aportes, permisos, aprobar miembros..."
              className="pl-9"
              data-testid="kb-search-input"
            />
          </div>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="sm:w-[240px]" data-testid="kb-module-filter">
              <SelectValue placeholder="Todos los módulos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los módulos</SelectItem>
              {modules.map(m => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList>
              <TabsTrigger value="article" data-testid="kb-tab-articles">
                <FileText className="w-4 h-4 mr-1.5" /> Guías de uso
              </TabsTrigger>
              <TabsTrigger value="video" data-testid="kb-tab-videos">
                <Video className="w-4 h-4 mr-1.5" /> Microtutoriales
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {isSuperAdmin && (
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" size="sm"
              onClick={openCreate} data-testid={kind === 'video' ? 'kb-new-video-button' : 'kb-new-article-button'}>
              <Plus className="w-4 h-4 mr-1" /> {kind === 'video' ? 'Nuevo microtutorial' : 'Nueva guía'}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="kb-empty">
            No encontramos contenido con esa búsqueda.
          </CardContent></Card>
        ) : kind === 'article' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4 space-y-2 lg:max-h-[70vh] lg:overflow-y-auto pr-1" data-testid="kb-article-list">
              {items.map(item => (
                <button
                  key={item.slug}
                  onClick={() => setSelected(item)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.slug === item.slug ? 'border-[#056A84] bg-[#056A84]/5' : 'hover:bg-muted/40'}`}
                  data-testid={`kb-article-${item.slug}`}
                >
                  <div className="flex items-start gap-2">
                    <BookOpen className="w-4 h-4 text-[#056A84] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.summary}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <Badge variant="secondary" className="text-[10px]">{label(item.module)}</Badge>
                        {item.is_published === false && (
                          <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100"
                            data-testid={`kb-draft-badge-${item.slug}`}>Borrador</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-8">
              {selected && (
                <Card data-testid="kb-article-detail">
                  <CardContent className="p-5 lg:p-6">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h2 className="text-base md:text-lg font-semibold text-foreground">{selected.title}</h2>
                      {isSuperAdmin && (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="kb-edit-article">
                            Editar
                          </Button>
                          <Button variant="outline" size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleting(selected)} data-testid="kb-delete-article">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{selected.summary}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(selected.tags || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                    <div className="mt-4 border-t pt-4">
                      <KbMarkdown text={selected.body} />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="kb-video-grid">
            {items.map(item => {
              return (
                <Card key={item.slug} className="overflow-hidden" data-testid={`kb-video-${item.slug}`}>
                  <div className="aspect-video bg-[#062B36] flex items-center justify-center">
                    <VideoMedia
                      item={item}
                      playing={playingSlug === item.slug}
                      onPlay={() => setPlayingSlug(item.slug)}
                    />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="text-[10px]">{label(item.module)}</Badge>
                      {!item.video_url && <Badge variant="outline" className="text-[10px]">Sin video</Badge>}
                      {item.is_published === false && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100"
                          data-testid={`kb-draft-badge-${item.slug}`}>Borrador</Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{item.summary}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {parseVideo(item.video_url)?.watch && (
                        <a
                          href={parseVideo(item.video_url).watch}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-[#056A84] underline underline-offset-2"
                          data-testid={`kb-video-open-${item.slug}`}
                        >
                          Ver en YouTube
                        </a>
                      )}
                      {isSuperAdmin && (
                        <>
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => openEdit(item)} data-testid={`kb-video-edit-${item.slug}`}>
                            <Link2 className="w-3.5 h-3.5 mr-1" /> {item.video_url ? 'Cambiar enlace' : 'Cargar enlace'}
                          </Button>
                          <Button variant="outline" size="sm"
                            className="text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleting(item)} data-testid={`kb-video-delete-${item.slug}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setForm(null); } }}>
        <DialogContent className="sm:max-w-2xl" data-testid="kb-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing?.isNew
                ? (editing.kind === 'video' ? 'Nuevo microtutorial' : 'Nueva guía de uso')
                : editing?.kind === 'video' ? 'Microtutorial' : 'Editar guía'}
            </DialogTitle>
            <DialogDescription>
              {editing?.kind === 'video'
                ? 'Pega el enlace de YouTube (incluidos Shorts), Vimeo, Loom, Google Drive o un archivo .mp4. Se muestra incrustado en el Centro de Ayuda.'
                : 'El contenido admite títulos con ##, listas con - y tablas con |.'}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4 py-1 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="kb-form-title" />
              </div>
              <div className="space-y-2">
                <Label>Resumen</Label>
                <Input value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} data-testid="kb-form-summary" />
              </div>
              <div className="space-y-2">
                <Label>Módulo</Label>
                <Select value={form.module} onValueChange={(v) => setForm({ ...form, module: v })}>
                  <SelectTrigger data-testid="kb-form-module"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(moduleLabels).map(m => (
                      <SelectItem key={m} value={m}>{label(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editing?.kind === 'video' ? (
                <div className="space-y-2">
                  <Label>Enlace del video</Label>
                  <Input
                    value={form.video_url}
                    onChange={e => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="font-mono text-xs"
                    data-testid="kb-form-video-url"
                  />
                  {form.video_url.trim() && (
                    embedUrl(form.video_url) ? (
                      <div className="rounded-lg overflow-hidden border aspect-video bg-[#062B36]" data-testid="kb-form-video-preview">
                        {/\.(mp4|webm|ogg)(\?|$)/i.test(embedUrl(form.video_url)) ? (
                          <video src={embedUrl(form.video_url)} controls className="w-full h-full" />
                        ) : (
                          <iframe
                            src={embedUrl(form.video_url)}
                            title="Vista previa"
                            className="w-full h-full"
                            allowFullScreen
                          />
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-2" data-testid="kb-form-video-warning">
                        No reconozco este enlace para mostrarlo dentro de la Suite. Usa YouTube
                        (incluidos Shorts), Vimeo, Loom, un archivo .mp4 o un video de Google Drive
                        compartido con "cualquiera con el enlace".
                      </p>
                    )
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Contenido</Label>
                  <textarea
                    value={form.body}
                    onChange={e => setForm({ ...form, body: e.target.value })}
                    rows={14}
                    className="w-full rounded-md border border-input bg-background p-3 text-xs font-mono"
                    data-testid="kb-form-body"
                  />
                </div>
              )}

              <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                <div>
                  <Label className="text-sm">Publicado</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {form.is_published
                      ? 'Visible para pastores y colaboradores.'
                      : 'Queda como borrador: solo tú lo ves hasta que lo publiques.'}
                  </p>
                </div>
                <Switch
                  checked={form.is_published}
                  onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                  data-testid="kb-form-published"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }} data-testid="kb-form-cancel">Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={save} disabled={saving} data-testid="kb-form-save">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Save className="w-4 h-4 mr-1" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="kb-delete-dialog">
          <DialogHeader>
            <DialogTitle>{deleting?.kind === 'video' ? 'Eliminar microtutorial' : 'Eliminar guía de uso'}</DialogTitle>
            <DialogDescription>
              Se quitará «{deleting?.title}» del Centro de Ayuda. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} data-testid="kb-delete-cancel">Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}
              disabled={saving} data-testid="kb-delete-confirm">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
